/**
 * Maps legacy localStorage entity keys to D1 API backends.
 * All reads/writes go to Cloudflare D1 — no browser persistence for business data.
 */
import { spaEntityAPI, formationAPI } from '../services/api';

const SPA_ENTITIES = new Set([
  'courses',
  'trainers',
  'classrooms',
  'training-sessions',
  'sessions',
  'certifications',
  'expenses',
  'notifications',
]);

const FORMATION_LIST = {
  students: () => formationAPI.getStudents({ limit: 500, page: 1 }).then((r) => r.items || []),
  batches: () => formationAPI.getBatches({}).then((r) => r.items || []),
  enrollments: () => formationAPI.getEnrollments({}).then((r) => r.items || []),
  payments: () => formationAPI.getFormationPayments({}).then((r) => r.items || []),
  evaluations: () => formationAPI.getEvaluations({}).then((r) => r.items || []),
  attendance: () => formationAPI.getAttendance({}).then((r) => r.items || []),
};

export async function fetchAllEntities(entityKey) {
  if (FORMATION_LIST[entityKey]) {
    return FORMATION_LIST[entityKey]();
  }
  if (SPA_ENTITIES.has(entityKey)) {
    return spaEntityAPI.list(entityKey);
  }
  throw new Error(`Unknown entity "${entityKey}" — no D1 backend configured.`);
}

export async function fetchEntityById(entityKey, id) {
  if (entityKey === 'students') return formationAPI.getStudentById(id);
  if (entityKey === 'batches') return formationAPI.getBatchById(id);
  if (SPA_ENTITIES.has(entityKey)) return spaEntityAPI.getById(entityKey, id);
  const all = await fetchAllEntities(entityKey);
  return all.find((item) => item.id === id) || null;
}

export async function createEntity(entityKey, item) {
  const payload = { ...item };
  if (!payload.id) payload.id = `${entityKey}-${Date.now()}`;
  if (!payload.createdAt) payload.createdAt = new Date().toISOString();

  if (entityKey === 'students') return formationAPI.createStudent(payload);
  if (entityKey === 'batches') return formationAPI.createBatch(payload);
  if (entityKey === 'enrollments') return formationAPI.createEnrollment(payload);
  if (entityKey === 'payments') return formationAPI.createFormationPayment(payload);
  if (entityKey === 'evaluations') return formationAPI.createEvaluation(payload);
  if (entityKey === 'attendance') return formationAPI.saveAttendance(payload);
  if (SPA_ENTITIES.has(entityKey)) return spaEntityAPI.create(entityKey, payload);
  throw new Error(`Cannot create entity "${entityKey}"`);
}

export async function updateEntity(entityKey, item) {
  if (entityKey === 'students') return formationAPI.updateStudent(item.id, item);
  if (entityKey === 'batches') return formationAPI.updateBatch(item.id, item);
  if (entityKey === 'payments') return formationAPI.updateFormationPayment(item.id, item);
  if (SPA_ENTITIES.has(entityKey)) return spaEntityAPI.update(entityKey, item.id, item);
  throw new Error(`Cannot update entity "${entityKey}"`);
}

export async function deleteEntity(entityKey, id) {
  if (entityKey === 'students') return formationAPI.deleteStudent(id);
  if (entityKey === 'batches') return formationAPI.deleteBatch(id);
  if (entityKey === 'enrollments') return formationAPI.deleteEnrollment(id);
  if (entityKey === 'payments') return formationAPI.deleteFormationPayment(id);
  if (entityKey === 'evaluations') return formationAPI.deleteEvaluation(id);
  if (SPA_ENTITIES.has(entityKey)) return spaEntityAPI.delete(entityKey, id);
  throw new Error(`Cannot delete entity "${entityKey}"`);
}

/** @deprecated Sync localStorage access removed — use fetchAllEntities / useEntityStore. */
export function getAllItems() {
  throw new Error('localStorage removed: use fetchAllEntities() or useEntityStore() with D1 API.');
}

export default {
  fetchAllEntities,
  fetchEntityById,
  createEntity,
  updateEntity,
  deleteEntity,
};
